import { ChevronDown, FolderOpen, Grid3X3, Search } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { setProjectId, clearProject } from '../../store/slices/projectSlice';
import { projectService } from '../../services/projectService';
import { Project } from '../../types';

interface HeaderProjectSelectorProps {
  className?: string;
}

export const HeaderProjectSelector: React.FC<HeaderProjectSelectorProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const currentProjectId = useSelector((state: RootState) => state.project.projectId);

  const [isOpen, setIsOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter projects based on search term
  const filteredProjects = projects.filter(
    project =>
      project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Load projects on open
  useEffect(() => {
    if (isOpen && projects.length === 0) {
      loadProjects();
    }
  }, [isOpen]);

  // Load current project details when projectId changes
  useEffect(() => {
    if (currentProjectId) {
      loadCurrentProject();
    } else {
      setCurrentProject(null);
    }
  }, [currentProjectId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      const totalItems = 1 + filteredProjects.length; // "View All Projects" + projects

      switch (e.key) {
        case 'Escape':
          setIsOpen(false);
          setHighlightedIndex(-1);
          e.preventDefault();
          break;
        case 'ArrowDown':
          setHighlightedIndex(prev => (prev < totalItems - 1 ? prev + 1 : 0));
          e.preventDefault();
          break;
        case 'ArrowUp':
          setHighlightedIndex(prev => (prev > 0 ? prev - 1 : totalItems - 1));
          e.preventDefault();
          break;
        case 'Enter':
          if (highlightedIndex === 0) {
            handleViewAllProjects();
          } else if (highlightedIndex > 0 && filteredProjects[highlightedIndex - 1]) {
            handleProjectSelect(filteredProjects[highlightedIndex - 1]);
          }
          e.preventDefault();
          break;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, highlightedIndex, filteredProjects]);

  // Reset highlighted index when search term changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await projectService.getProjects({
        page: 1,
        limit: 50, // Load recent projects
        sortBy: 'updatedAt',
        sortOrder: 'desc',
      });

      if (response.success && response.data?.projects) {
        setProjects(response.data.projects);
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentProject = async () => {
    if (!currentProjectId) return;

    try {
      // Check if project is already in the list
      const existingProject = projects.find(p => (p.id || p._id) === currentProjectId);
      if (existingProject) {
        setCurrentProject(existingProject);
        return;
      }

      // Load project details
      const response = await projectService.getProject(currentProjectId);
      if (response.success && response.data) {
        setCurrentProject(response.data);
      }
    } catch (error) {
      console.error('Failed to load current project:', error);
      // Clear invalid project ID
      dispatch(clearProject());
    }
  };

  const getCurrentModule = () => {
    const path = location.pathname;
    const match = path.match(/\/projects\/[^/]+\/(.+)/);
    return match ? match[1] : 'dashboard';
  };

  const handleProjectSelect = (project: Project) => {
    const projectId = project.id || project._id;
    const currentModule = getCurrentModule();

    // Update Redux state
    dispatch(setProjectId(projectId));

    // Navigate maintaining current module, or fallback to dashboard
    if (location.pathname.startsWith('/projects/')) {
      navigate(`/projects/${projectId}/${currentModule}`);
    } else {
      navigate(`/projects/${projectId}/dashboard`);
    }

    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleViewAllProjects = () => {
    navigate('/projects');
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleClearProject = () => {
    dispatch(clearProject());
    navigate('/dashboard');
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <FolderOpen className="h-4 w-4 mr-2" />
        <span>
          {currentProject ? (
            <span className="max-w-32 truncate">{currentProject.name}</span>
          ) : (
            'Projects'
          )}
        </span>
        <ChevronDown
          className={`ml-1 h-4 w-4 transform transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-900">Select Project</h3>
          </div>

          {/* Search */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                autoFocus
              />
            </div>
          </div>

          {/* Action Items */}
          <div className="py-2">
            {/* View All Projects */}
            <button
              type="button"
              onClick={handleViewAllProjects}
              className={`w-full flex items-center px-4 py-3 text-sm transition-colors ${
                highlightedIndex === 0
                  ? 'bg-brand-50 text-brand-900'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <Grid3X3 className="h-4 w-4 mr-3 text-gray-400" />
              <div className="text-left">
                <div className="font-medium">View all projects</div>
                <div className="text-xs text-gray-500">Browse and manage all projects</div>
              </div>
            </button>

            {/* Organization Dashboard (if project selected) */}
            {currentProjectId && (
              <button
                type="button"
                onClick={handleClearProject}
                className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
              >
                <div className="h-4 w-4 mr-3" /> {/* Spacer */}
                <div className="text-left">
                  <div className="font-medium">Organization Dashboard</div>
                  <div className="text-xs text-gray-500">View org-wide dashboard</div>
                </div>
              </button>
            )}
          </div>

          {/* Projects List */}
          <div className="border-t border-gray-200 max-h-64 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-8 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Loading projects...</p>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <FolderOpen className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  {searchTerm ? 'No projects found' : 'No projects available'}
                </p>
              </div>
            ) : (
              <div className="py-2">
                {filteredProjects.map((project, index) => {
                  const projectId = project.id || project._id;
                  const isSelected = projectId === currentProjectId;
                  const isHighlighted = highlightedIndex === index + 1; // +1 because "View All Projects" is index 0

                  return (
                    <button
                      key={projectId}
                      type="button"
                      onClick={() => handleProjectSelect(project)}
                      className={`w-full flex items-center px-4 py-3 text-sm transition-colors ${
                        isHighlighted
                          ? 'bg-brand-100 text-brand-900'
                          : isSelected
                            ? 'bg-brand-50 text-brand-900'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                          isSelected ? 'bg-brand-100' : 'bg-gray-100'
                        }`}
                      >
                        <FolderOpen
                          className={`h-4 w-4 ${isSelected ? 'text-brand-600' : 'text-gray-500'}`}
                        />
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div
                          className={`font-medium truncate ${
                            isSelected ? 'text-brand-900' : 'text-gray-900'
                          }`}
                        >
                          {project.name}
                        </div>
                        {project.location?.city && (
                          <div className="text-xs text-gray-500 truncate mt-0.5">
                            {project.location.city},{' '}
                            {project.location.state || project.location.country}
                          </div>
                        )}
                      </div>
                      {isSelected && <div className="w-2 h-2 bg-brand-600 rounded-full ml-2"></div>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
